// Sugalarm — screen-brightness bridge wrapper (cycle 197).
//
// The single owner of access to the native `ScreenBrightness` plugin.
// That plugin exists ONLY in the Android kiosk APK (the `kiosk` product
// flavor); it is absent on web, the Pi kiosk, iPhone, the phone layout,
// and the `standard` Android flavor — so `isAvailable()` is false on every
// surface except the kiosk APK, and the Settings → Display Brightness card
// gates strictly on it.
//
// Like every native plugin in the pure-remote webview (cycle 26/138), it is
// reached through the bridge-injected `window.Capacitor.Plugins` proxy —
// NEVER a bare `import("@capacitor/...")`, which has no bundler to resolve
// against at runtime. Mirrors the `capacitorPlugin<T>()` helper in
// `lib/capacitor.ts`.
//
// Brightness config is TABLET-LOCAL: it lives in the tablet's
// SharedPreferences via the native plugin, and is never sent to the server
// (no `/api/settings` key exists for it by construction), the phone, or the
// Pi.

import { isCapacitorNative } from "./capacitor";

/** The legible floor (15%). Clamped in BOTH the UI and the native plugin —
 *  defense in depth so the alarm screen can never go unreadable. */
export const MIN_LEGIBLE = 0.15;

export interface ScreenBrightnessConfig {
  /** Daytime backlight, 0.0–1.0 (floored to MIN_LEGIBLE). */
  dayLevel: number;
  /** When true, the screen eases to `dimLevel` inside the from/until window. */
  autoDim: boolean;
  /** Local minutes-of-day the dim window starts (0–1439). */
  fromMinutes: number;
  /** Local minutes-of-day the dim window ends (0–1439). `until <= from`
   *  wraps past midnight. */
  untilMinutes: number;
  /** Overnight backlight, 0.0–1.0 (floored to MIN_LEGIBLE). */
  dimLevel: number;
}

interface ScreenBrightnessProxy {
  getConfig(): Promise<ScreenBrightnessConfig>;
  setConfig(cfg: ScreenBrightnessConfig): Promise<ScreenBrightnessConfig>;
}

/** Resolve the natively-registered plugin proxy off the Capacitor bridge,
 *  or null on any surface where it isn't present. */
function plugin(): ScreenBrightnessProxy | null {
  if (!isCapacitorNative()) return null;
  const cap = (
    window as { Capacitor?: { Plugins?: Record<string, unknown> } }
  ).Capacitor;
  const p = cap?.Plugins?.ScreenBrightness;
  return p != null ? (p as ScreenBrightnessProxy) : null;
}

/** True iff the native ScreenBrightness plugin is registered on the bridge —
 *  i.e. the SPA is running inside the Android kiosk APK. The Settings card
 *  renders only when this is true. */
export function isAvailable(): boolean {
  return plugin() != null;
}

/** Read the tablet-local brightness config, or null on a non-kiosk-APK
 *  surface / on any bridge error. */
export async function getConfig(): Promise<ScreenBrightnessConfig | null> {
  const p = plugin();
  if (p == null) return null;
  try {
    return await p.getConfig();
  } catch (err) {
    console.warn("[screenBrightness] getConfig failed", err);
    return null;
  }
}

/** Write the tablet-local brightness config. No-ops on a non-kiosk-APK
 *  surface. Tablet-local — NOT a `/api/settings` PATCH, no WS broadcast. */
export async function setConfig(cfg: ScreenBrightnessConfig): Promise<void> {
  const p = plugin();
  if (p == null) return;
  try {
    await p.setConfig(cfg);
  } catch (err) {
    console.warn("[screenBrightness] setConfig failed", err);
  }
}
